import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

// 판매가 이력 대시보드 (2026-07-26)
// 날짜별: 대행원가 + 그날 정책으로 계산된 판매가 + 실제 판매실적 + 적용 마진/기준가.
// + 정책(수식·마진) 변천 타임라인 + 원가vs판매가 추이 차트.
const won = (n) => (n == null || isNaN(n)) ? '-' : Number(n).toLocaleString('ko-KR') + '원';

export default function PriceHistoryScreen() {
  const [rows, setRows] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [brand, setBrand] = useState('CGV');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const d = await (await fetch('/request/priceHistory?' + qs.toString(), { credentials: 'include' })).json();
      setRows(d.ok ? d.rows : []);
      setTimeline(d.ok ? (d.timeline || []) : []);
    } catch (e) { setRows([]); setTimeline([]); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // 차트: 선택 브랜드의 원가 vs 판매가 (날짜 오름차순)
  const chartRows = [...rows].reverse();
  const chartData = {
    labels: chartRows.map(r => r.date),
    datasets: [
      { label: '대행원가', data: chartRows.map(r => (r.costEff?.[brand] ?? r.cost?.[brand]) ?? null), borderColor: '#90a4ae', backgroundColor: '#90a4ae', tension: 0.2 },
      { label: '판매가', data: chartRows.map(r => r.sale?.[brand] ?? null), borderColor: '#1976d2', backgroundColor: '#1976d2', tension: 0.2 },
    ],
  };
  const chartOptions = { responsive: true, plugins: { legend: { position: 'top' } }, scales: { y: { ticks: { callback: v => Number(v).toLocaleString() + '원' } } } };

  const th = { textAlign: 'right', padding: '8px 10px', fontSize: 12, color: '#666', borderBottom: '2px solid #e5e5e5', whiteSpace: 'nowrap' };
  const td = { textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid #eee', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ padding: 24, width: '100%', maxWidth: 1200 }}>
      <h2 style={{ margin: '0 0 4px' }}>판매가 이력</h2>
      <p style={{ color: '#777', fontSize: 13, margin: '0 0 18px' }}>
        날짜별 대행원가와 그날 계산된 판매가, 실제 판매 실적, 그때 적용된 마진·수식을 한눈에 봅니다.
      </p>

      {/* 필터 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc' }} />
        <span style={{ color: '#999' }}>~</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc' }} />
        <button onClick={load} style={{ padding: '6px 16px', borderRadius: 6, border: 0, background: '#333', color: '#fff', fontSize: 13, cursor: 'pointer' }}>조회</button>
        <span style={{ marginLeft: 16, color: '#666', fontSize: 13 }}>차트 브랜드:</span>
        {['CGV', '롯데시네마', '메가박스'].map(b => (
          <button key={b} onClick={() => setBrand(b)}
            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #ccc', background: brand === b ? '#1976d2' : '#fff', color: brand === b ? '#fff' : '#555', fontSize: 13, cursor: 'pointer' }}>{b}</button>
        ))}
      </div>

      {/* 차트: 원가 vs 판매가 추이 */}
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 10, padding: 18, marginBottom: 22 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 12 }}>원가 vs 판매가 추이 ({brand})</div>
        {chartRows.length > 0 ? <Line data={chartData} options={chartOptions} /> : <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>{loading ? '불러오는 중…' : '데이터가 없습니다'}</div>}
      </div>

      {/* 표1: 날짜별 원가·계산판매가·마진·실판매 */}
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 10, padding: 18, marginBottom: 22 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 12 }}>날짜별 원가 · 계산 판매가 · 실제 판매</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>날짜</th>
              <th style={th}>CGV 원가</th><th style={th}>CGV 판매가</th>
              <th style={th}>롯데 원가</th><th style={th}>롯데 판매가</th>
              <th style={th}>메가 원가</th><th style={th}>메가 판매가</th>
              <th style={th}>적용마진</th>
              <th style={th}>실판매 건수</th><th style={th}>판매합</th><th style={th}>순이익합</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{r.date}</td>
                  {/* [장실장님 MAX 2026-08-08] 대행원가 = 영화거래소로 넘어가는 값(costEff, 장실장님 MAX 반영).
                      예매금액설정 화면과 동일 기준으로 통일. costEff 없으면(구데이터) 원본 cost 폴백. */}
                  <td style={td}>{won(r.costEff?.CGV ?? r.cost?.CGV)}</td><td style={{ ...td, color: '#1976d2', fontWeight: 700 }}>{won(r.sale?.CGV)}</td>
                  <td style={td}>{won(r.costEff?.['롯데시네마'] ?? r.cost?.['롯데시네마'])}</td><td style={{ ...td, color: '#1976d2', fontWeight: 700 }}>{won(r.sale?.['롯데시네마'])}</td>
                  <td style={td}>{won(r.costEff?.['메가박스'] ?? r.cost?.['메가박스'])}</td><td style={{ ...td, color: '#1976d2', fontWeight: 700 }}>{won(r.sale?.['메가박스'])}</td>
                  <td style={td}>{r.margin != null ? Math.round(r.margin * 100) + '%' : '-'}</td>
                  <td style={td}>{r.sold ? r.sold.cnt + '건' : '-'}</td>
                  <td style={td}>{r.sold ? won(r.sold.sale_sum) : '-'}</td>
                  <td style={{ ...td, fontWeight: 700, color: r.sold && r.sold.profit_sum < 0 ? '#dc2626' : '#16a34a' }}>{r.sold ? won(r.sold.profit_sum) : '-'}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={11} style={{ padding: 20, textAlign: 'center', color: '#999' }}>{loading ? '불러오는 중…' : '데이터가 없습니다'}</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>* 정책 미설정 기간은 원가=판매가. 실판매는 주문에 스냅샷이 쌓인 신규 주문부터 표시.</div>
      </div>

      {/* 표2: 정책(수식·마진) 변천 타임라인 */}
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 10, padding: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 12 }}>적용 수식 · 마진 변천 이력</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              <th style={{ ...th, textAlign: 'left' }}>적용 시작</th><th style={{ ...th, textAlign: 'left' }}>종료</th>
              <th style={th}>마진율</th><th style={th}>기준가</th>
              <th style={{ ...th, textAlign: 'left' }}>판매가 수식</th><th style={th}>상태</th>
            </tr></thead>
            <tbody>
              {timeline.map((t, i) => (
                <tr key={i}>
                  <td style={{ ...td, textAlign: 'left' }}>{t.from || '-'}</td>
                  <td style={{ ...td, textAlign: 'left' }}>{t.to || (t.active ? '현재' : '-')}</td>
                  <td style={td}>{Math.round((t.margin || 0) * 100)}%</td>
                  <td style={td}>{won(t.base)}</td>
                  <td style={{ ...td, textAlign: 'left', fontFamily: 'monospace', fontSize: 12 }}>{t.sale_formula || '-'}</td>
                  <td style={td}>{t.active ? <span style={{ color: '#16a34a', fontWeight: 700 }}>적용중</span> : '종료'}</td>
                </tr>
              ))}
              {timeline.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#999' }}>아직 저장된 정책이 없습니다. 예매금액설정에서 마진을 저장하면 여기에 이력이 쌓입니다.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
